/**
 * Unified Gallery Data Access Layer
 * 
 * This module provides a unified interface for accessing gallery images,
 * abstracting away the complexity of the dual image system (gallery_photos + franchise_image_jobs).
 * 
 * Migration Strategy: This layer will eventually replace direct gallery_photos access,
 * providing a single source of truth for all gallery image operations.
 */

import { supabase } from '@/lib/supabase';
import type { GalleryPhoto } from '../types';

export interface UnifiedGalleryImage {
  image_type: 'job_image' | 'gallery_photo';
  original_id: number;
  gallery_id: number;
  image_url: string;
  caption: string | null;
  review_status: 'pending' | 'under_review' | 'approved' | 'rejected';
  created_at: string;
  
  // Job image specific fields
  job_id?: number;
  job_number?: number;
  franchise_region?: string;
  is_cover?: boolean;
  sort_order?: number;
  file_name?: string;
  jobber_web_uri?: string;
}

export interface GalleryImageUpload {
  galleryId: number;
  file: File;
  caption?: string;
  jobNumber?: number;
  franchiseRegion?: string;
}

type LinkedGalleryJobImageRow = {
  id: number;
  gallery_id: number;
  is_cover: boolean | null;
  sort_order: number | null;
  created_at: string;
  franchise_image_jobs: {
    id: number;
    image_url: string | null;
    file_name: string | null;
    job_id: number | null;
    job_number: number | null;
    franchise_region: string | null;
    review_status: UnifiedGalleryImage['review_status'] | null;
    created_on: string | null;
    jobber_web_uri: string | null;
  } | null;
};

function normalizeFranchiseImage(
  franchiseImage: LinkedGalleryJobImageRow['franchise_image_jobs'] | LinkedGalleryJobImageRow['franchise_image_jobs'][] | null,
): LinkedGalleryJobImageRow['franchise_image_jobs'] {
  if (Array.isArray(franchiseImage)) {
    return franchiseImage[0] ?? null;
  }

  return franchiseImage;
}

function toUnifiedGalleryImage(item: LinkedGalleryJobImageRow): UnifiedGalleryImage {
  const franchiseImage = normalizeFranchiseImage(item.franchise_image_jobs);

  return {
    image_type: 'job_image',
    original_id: item.id,
    gallery_id: item.gallery_id,
    image_url: franchiseImage?.image_url || '',
    caption: franchiseImage?.file_name || '',
    review_status: (franchiseImage?.review_status || 'pending') as UnifiedGalleryImage['review_status'],
    created_at: franchiseImage?.created_on || item.created_at,
    job_id: franchiseImage?.id ?? undefined,
    job_number: franchiseImage?.job_number ?? undefined,
    franchise_region: franchiseImage?.franchise_region ?? undefined,
    is_cover: item.is_cover ?? undefined,
    sort_order: item.sort_order ?? undefined,
    file_name: franchiseImage?.file_name ?? undefined,
    jobber_web_uri: franchiseImage?.jobber_web_uri ?? undefined,
  };
}

export class GalleryDataAccess {
  private static async fetchLinkedJobImages(
    galleryIds: number[],
  ): Promise<LinkedGalleryJobImageRow[]> {
    if (galleryIds.length === 0) {
      return [];
    }

    const { data, error } = await supabase
      .from('gallery_job_images')
      .select(`
        id,
        gallery_id,
        is_included,
        is_cover,
        sort_order,
        created_at,
        franchise_image_jobs!inner(
          id,
          image_url,
          file_name,
          job_id,
          job_number,
          franchise_region,
          review_status,
          created_on,
          jobber_web_uri
        )
      `)
      .in('gallery_id', galleryIds)
      .eq('is_included', true)
      .order('sort_order', { ascending: true });

    if (error) {
      throw error;
    }

    return ((data ?? []) as unknown as LinkedGalleryJobImageRow[]).map((row) => ({
      ...row,
      franchise_image_jobs: normalizeFranchiseImage(row.franchise_image_jobs),
    }));
  }
  
  /**
   * Primary method: Get all images for a gallery using the unified approach
   * This queries franchise_image_jobs linked through gallery_job_images
   */
  static async getGalleryImages(galleryId: number): Promise<UnifiedGalleryImage[]> {
    try {
      const imagesByGalleryId = await this.getGalleryImagesByGalleryIds([galleryId]);
      const jobImages = imagesByGalleryId.get(galleryId) ?? [];

      console.log(`✅ Fetched ${jobImages.length} job images for gallery ${galleryId}`);
      return jobImages;

    } catch (error) {
      console.error('Error in getGalleryImages:', error);
      return [];
    }
  }

  /**
   * Bulk fetch gallery images for multiple galleries in one query.
   * Returns a map keyed by gallery id so callers can avoid N+1 query patterns.
   */
  static async getGalleryImagesByGalleryIds(
    galleryIds: number[],
  ): Promise<Map<number, UnifiedGalleryImage[]>> {
    const uniqueGalleryIds = [...new Set(galleryIds)];
    const imagesByGalleryId = new Map<number, UnifiedGalleryImage[]>();

    uniqueGalleryIds.forEach((galleryId) => {
      imagesByGalleryId.set(galleryId, []);
    });

    if (uniqueGalleryIds.length === 0) {
      return imagesByGalleryId;
    }

    try {
      const linkedImages = await this.fetchLinkedJobImages(uniqueGalleryIds);

      for (const row of linkedImages) {
        const galleryImages = imagesByGalleryId.get(row.gallery_id);
        if (!galleryImages) {
          continue;
        }

        galleryImages.push(toUnifiedGalleryImage(row));
      }

      return imagesByGalleryId;
    } catch (error) {
      console.error('Error in getGalleryImagesByGalleryIds:', error);
      return imagesByGalleryId;
    }
  }

  /**
   * Legacy compatibility method: Get gallery photos in old format
   * This method transforms unified images back to the legacy GalleryPhoto format
   * for backward compatibility during the migration period
   */
  static async getGalleryPhotos(galleryId: number): Promise<GalleryPhoto[]> {
    const unifiedImages = await this.getGalleryImages(galleryId);
    
    // Transform to legacy format
    return unifiedImages.map(img => ({
      id: img.original_id,
      gallery_id: img.gallery_id,
      image_url: img.image_url,
      caption: img.caption,
      sort_order: img.sort_order || 0,
      created_at: img.created_at
    }));
  }

  /**
   * Legacy compatibility method: bulk fetch gallery photos in the old format.
   */
  static async getGalleryPhotosByGalleryIds(
    galleryIds: number[],
  ): Promise<Map<number, GalleryPhoto[]>> {
    const imagesByGalleryId = await this.getGalleryImagesByGalleryIds(galleryIds);
    const photosByGalleryId = new Map<number, GalleryPhoto[]>();

    for (const [galleryId, images] of imagesByGalleryId.entries()) {
      photosByGalleryId.set(
        galleryId,
        images.map((img) => ({
          id: img.original_id,
          gallery_id: img.gallery_id,
          image_url: img.image_url,
          caption: img.caption,
          sort_order: img.sort_order || 0,
          created_at: img.created_at,
        })),
      );
    }

    return photosByGalleryId;
  }

  /**
   * Create a new gallery image by uploading to franchise_image_jobs system
   * This replaces the old gallery_photos upload workflow
   */
  static async uploadGalleryImage(upload: GalleryImageUpload): Promise<UnifiedGalleryImage> {
    const { galleryId, file, caption, jobNumber, franchiseRegion } = upload;

    try {
      // 1. Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `galleries/${galleryId}/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('gallery-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // 2. Get public URL
      const { data: urlData } = supabase.storage
        .from('gallery-images')
        .getPublicUrl(filePath);

      // 3. Create franchise_image_jobs record
      const { data: jobImageData, error: jobError } = await supabase
        .from('franchise_image_jobs')
        .insert({
          job_id: `DIRECT_UPLOAD_${galleryId}_${Date.now()}`,
          job_number: jobNumber || 0,
          image_url: urlData.publicUrl,
          file_name: caption || file.name,
          franchise_region: franchiseRegion || 'general',
          review_status: 'approved', // Direct uploads are auto-approved
          status: 'active',
          created_on: new Date().toISOString()
        })
        .select()
        .single();

      if (jobError) throw jobError;

      // 4. Link to gallery
      const { data: linkData, error: linkError } = await supabase
        .from('gallery_job_images')
        .insert({
          gallery_id: galleryId,
          job_id: jobImageData.id,
          is_included: true,
          is_cover: false,
          sort_order: 0
        })
        .select()
        .single();

      if (linkError) throw linkError;

      // 5. Return unified format
      const unifiedImage: UnifiedGalleryImage = {
        image_type: 'job_image',
        original_id: linkData.id,
        gallery_id: galleryId,
        image_url: jobImageData.image_url,
        caption: jobImageData.file_name,
        review_status: jobImageData.review_status as any,
        created_at: jobImageData.created_on,
        job_id: jobImageData.id,
        job_number: jobImageData.job_number,
        franchise_region: jobImageData.franchise_region,
        is_cover: false,
        sort_order: 0,
        file_name: jobImageData.file_name
      };

      console.log('✅ Successfully uploaded gallery image via unified system');
      return unifiedImage;

    } catch (error) {
      console.error('Error uploading gallery image:', error);
      throw error;
    }
  }

  /**
   * Delete a gallery image
   * For job images, this removes the link from gallery_job_images
   * For legacy gallery_photos, this deletes the record
   */
  static async deleteGalleryImage(imageId: number, imageType: 'job_image' | 'gallery_photo'): Promise<void> {
    try {
      if (imageType === 'job_image') {
        // Remove link from gallery (don't delete the actual franchise image)
        const { error } = await supabase
          .from('gallery_job_images')
          .delete()
          .eq('id', imageId);

        if (error) throw error;
        console.log('✅ Removed job image link from gallery');
      } else {
        // Legacy: Delete from gallery_photos
        const { error } = await supabase
          .from('gallery_photos')
          .delete()
          .eq('id', imageId);

        if (error) throw error;
        console.log('✅ Deleted legacy gallery photo');
      }
    } catch (error) {
      console.error('Error deleting gallery image:', error);
      throw error;
    }
  }

  /**
   * Update image caption/metadata
   */
  static async updateImageCaption(imageId: number, caption: string, imageType: 'job_image' | 'gallery_photo'): Promise<void> {
    try {
      if (imageType === 'job_image') {
        // Look up the underlying franchise image id before updating its caption
        const { data: linkRecord, error: linkError } = await supabase
          .from('gallery_job_images')
          .select('job_id')
          .eq('id', imageId)
          .single();

        if (linkError) throw linkError;
        if (!linkRecord?.job_id) {
          throw new Error(`Job image link ${imageId} is missing job_id`);
        }

        const { error } = await supabase
          .from('franchise_image_jobs')
          .update({ file_name: caption })
          .eq('id', linkRecord.job_id);

        if (error) throw error;
      } else {
        // Legacy: Update gallery_photos caption
        const { error } = await supabase
          .from('gallery_photos')
          .update({ caption })
          .eq('id', imageId);

        if (error) throw error;
      }
      console.log('✅ Updated image caption');
    } catch (error) {
      console.error('Error updating image caption:', error);
      throw error;
    }
  }

  /**
   * Set gallery cover image
   */
  static async setCoverImage(galleryId: number, imageUrl: string): Promise<void> {
    try {
      // Update gallery cover_image field
      const { error: galleryError } = await supabase
        .from('galleries')
        .update({ cover_image: imageUrl })
        .eq('id', galleryId);

      if (galleryError) throw galleryError;

      // Update gallery_job_images to mark the specific image as cover
      const { error: coverError } = await supabase
        .from('gallery_job_images')
        .update({ is_cover: false })
        .eq('gallery_id', galleryId);

      if (coverError) throw coverError;

      // Set the new cover image
      const { data: imageData } = await supabase
        .from('gallery_job_images')
        .select('id, franchise_image_jobs!inner(image_url)')
        .eq('gallery_id', galleryId);

      const targetImage = imageData?.find(img => (img.franchise_image_jobs as any)?.image_url === imageUrl);
      
      if (targetImage) {
        const { error: setCoverError } = await supabase
          .from('gallery_job_images')
          .update({ is_cover: true })
          .eq('id', targetImage.id);

        if (setCoverError) throw setCoverError;
      }

      console.log('✅ Set gallery cover image');
    } catch (error) {
      console.error('Error setting cover image:', error);
      throw error;
    }
  }

  /**
   * Get gallery statistics
   */
  static async getGalleryStats(galleryId: number): Promise<{
    totalImages: number;
    approvedImages: number;
    pendingImages: number;
    rejectedImages: number;
  }> {
    try {
      const images = await this.getGalleryImages(galleryId);
      
      const stats = {
        totalImages: images.length,
        approvedImages: images.filter(img => img.review_status === 'approved').length,
        pendingImages: images.filter(img => img.review_status === 'pending').length,
        rejectedImages: images.filter(img => img.review_status === 'rejected').length
      };

      return stats;
    } catch (error) {
      console.error('Error getting gallery stats:', error);
      return { totalImages: 0, approvedImages: 0, pendingImages: 0, rejectedImages: 0 };
    }
  }
}

// Export for convenient access
export const galleryDataAccess = GalleryDataAccess;
export default GalleryDataAccess;
